/**
 * Campañas visuales del login (lado izquierdo).
 * Fechas en calendario México; la primera coincidencia gana (orden = prioridad).
 * Imágenes en /public/login-banners/ — si falta el archivo, cae a default.
 * (No usar /public/login/: colisiona con la ruta SPA /login y nginx responde 403.)
 */

const DEFAULT_IMAGE = '/login-banners/login-default.png'

/** Mes/día inclusivos; soporta rangos que cruzan año (start > end en comparación MMDD). */
function inMonthDayRange(date, startMD, endMD) {
  const md = (date.getMonth() + 1) * 100 + date.getDate()
  const [sm, sd] = startMD
  const [em, ed] = endMD
  const start = sm * 100 + sd
  const end = em * 100 + ed
  if (start <= end) return md >= start && md <= end
  // Cruza año (ej. 28/12 → 4/1)
  return md >= start || md <= end
}

function nthWeekdayOfMonth(year, monthIndex, weekday, nth) {
  // weekday: 0=Dom … 6=Sáb; nth: 1..4 o -1 = último
  if (nth === -1) {
    const last = new Date(year, monthIndex + 1, 0)
    const diff = (last.getDay() - weekday + 7) % 7
    return new Date(year, monthIndex, last.getDate() - diff)
  }
  const first = new Date(year, monthIndex, 1)
  const offset = (weekday - first.getDay() + 7) % 7
  return new Date(year, monthIndex, 1 + offset + (nth - 1) * 7)
}

function aroundDay(date, monthIndex, day, padDays = 2) {
  const y = date.getFullYear()
  const t0 = new Date(y, monthIndex, day - padDays)
  const t1 = new Date(y, monthIndex, day + padDays)
  const t = new Date(y, date.getMonth(), date.getDate())
  return t >= t0 && t <= t1
}

/**
 * @typedef {object} LoginCampaign
 * @property {string} id
 * @property {string} title
 * @property {string} subtitle
 * @property {string} image
 * @property {string} accent  CSS color for form accents
 */

/** @type {Array<{ id: string, title: string, subtitle: string, image: string, accent: string, matches: (d: Date) => boolean }>} */
export const LOGIN_CAMPAIGNS = [
  {
    id: 'reyes',
    title: 'Día de Reyes',
    subtitle: 'Arithmos te acompaña este inicio de año',
    image: '/login-banners/login-reyes.png',
    accent: '#1e4d8c',
    matches: (d) => aroundDay(d, 0, 6, 1) || aroundDay(d, 0, 5, 0),
  },
  {
    id: 'anio_nuevo',
    title: 'Feliz año nuevo',
    subtitle: 'Empieza el año con claridad financiera',
    image: '/login-banners/login-anio-nuevo.png',
    accent: '#0b1f3a',
    matches: (d) => inMonthDayRange(d, [12, 28], [1, 4]),
  },
  {
    id: 'navidad',
    title: 'Felices fiestas',
    subtitle: 'Gracias por confiar en Arithmos',
    image: '/login-banners/login-navidad.png',
    accent: '#0d5c4d',
    matches: (d) => inMonthDayRange(d, [12, 1], [12, 27]),
  },
  {
    id: 'mujer',
    title: 'Día de la Mujer',
    subtitle: 'Celebramos a quienes impulsan cada negocio',
    image: '/login-banners/login-mujer.png',
    accent: '#c45c4a',
    matches: (d) => aroundDay(d, 2, 8, 2),
  },
  {
    id: 'nino',
    title: 'Día del Niño',
    subtitle: 'Por el futuro que hoy contabilizamos',
    image: '/login-banners/login-nino.png',
    accent: '#2a6fad',
    matches: (d) => aroundDay(d, 3, 30, 2),
  },
  {
    id: 'madres',
    title: 'Día de las Madres',
    subtitle: 'Honramos a quien sostiene el día a día',
    image: '/login-banners/login-madres.png',
    accent: '#b85c38',
    matches: (d) => aroundDay(d, 4, 10, 2),
  },
  {
    id: 'padre',
    title: 'Día del Padre',
    subtitle: 'Por quienes construyen con constancia',
    image: '/login-banners/login-padre.png',
    accent: '#3d4f5f',
    matches: (d) => {
      const padre = nthWeekdayOfMonth(d.getFullYear(), 5, 0, 3) // 3er domingo de junio (MX)
      const t = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      const t0 = new Date(padre)
      t0.setDate(padre.getDate() - 2)
      const t1 = new Date(padre)
      t1.setDate(padre.getDate() + 2)
      return t >= t0 && t <= t1
    },
  },
  {
    id: 'perro',
    title: 'Día del Perro',
    subtitle: 'Fieles como tu control contable',
    image: '/login-banners/login-perro.png',
    accent: '#8a5a2b',
    matches: (d) => aroundDay(d, 7, 26, 2),
  },
]

export const DEFAULT_LOGIN_CAMPAIGN = {
  id: 'default',
  title: 'Arithmos',
  subtitle: 'Sistema contable y financiero',
  image: DEFAULT_IMAGE,
  accent: '#0f6b5c',
}

/**
 * @param {Date} [now]
 * @returns {typeof DEFAULT_LOGIN_CAMPAIGN}
 */
export function getActiveLoginCampaign(now = new Date()) {
  for (const campaign of LOGIN_CAMPAIGNS) {
    if (campaign.matches(now)) {
      return {
        id: campaign.id,
        title: campaign.title,
        subtitle: campaign.subtitle,
        image: campaign.image,
        accent: campaign.accent,
      }
    }
  }
  return { ...DEFAULT_LOGIN_CAMPAIGN }
}
